/**
 * `main.tsx` — the Vite entry point (frontend.md §5 step 1).
 *
 * Mounts `<AppRoot>` into `#root`. Deliberately minimal — all the provider/router/shell
 * composition lives in `<AppRoot>`. `<StrictMode>` is on in dev (it double-invokes
 * effects to surface accidental impurities — the boot capability fetch and the
 * `ShellMount` seeding are both written to be StrictMode-safe: the fetch is `cancelled`-
 * guarded, the seeding returns a teardown).
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AppRoot } from './AppRoot';
import { installVersionGlobals } from './version';
import { applyUiScale, loadSavedUiScale } from './surfaces/settings/UiScaleSettings';
import { applyEditorFont, loadSavedEditorFont } from './theme/editorFonts';
import './styles/tokens.css';
import './styles/fonts.css';
// Self-hosted brand + editor-picker webfonts (generated by scripts/vendor_fonts.py).
// Local-first: nothing is fetched from a font CDN at runtime.
import './styles/brand-fonts.css';
import './styles/app.css';
// Loaded AFTER app.css: pale-theme (light/lunar) chrome + notebook
// active-state overrides (facelift-v3 guide Phase 2 + 7).
import './styles/_theme-chrome-overrides.css';

// Install the build sha + timestamp on `window.__ALETHEIA_VERSION__` and log them
// once on boot. Runs BEFORE React mounts so the version line is the first thing in
// DevTools — saves the "is the new bundle actually live?" round-trip we hit on
// 2026-05-18 (the Lexical #113 crash from a stale bundle).
installVersionGlobals();

// Apply the saved global UI scale (per-device, localStorage) BEFORE React mounts, so the first
// paint is already at the user's chosen size — no flash at 100% then a jump. The Settings slider
// (UiScaleSettings) is the live control; this is the boot-time application of the persisted value.
applyUiScale(loadSavedUiScale());

// Apply the saved editor-content font the same way (per-device; --font-editor + lazy font
// stylesheet) so the first note paint is already in the chosen face. Settings → Editor font
// is the live control.
applyEditorFont(loadSavedEditorFont());

// v0.1.14: the direct-to-Azure chat path is now configured AFTER Clerk loads a
// user (in McpIntegration, and post-mint), because token storage is keyed by
// workspace and we don't know which workspace this user belongs to until Clerk
// resolves. Boot-time chat goes through the same-origin SWA proxy (functional
// but doesn't stream); the first time the user opens Settings → MCP, the direct
// path engages and persists across launches via the per-workspace stored token.

const container = document.getElementById('root');
if (container === null) {
  throw new Error('Aletheia: #root element not found in index.html');
}

createRoot(container).render(
  <StrictMode>
    <AppRoot />
  </StrictMode>,
);
